import React, { useState, useEffect } from 'react';
import {
  Download, FileSpreadsheet, Database, HardDrive, Loader2,
  CheckCircle, ShieldCheck, FileText, RefreshCw, Info,
  Lock, KeyRound, Eye, EyeOff, X,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

/**
 * Pull the filename out of a Content-Disposition header, falling back to a
 * sensible default. Handles both `filename="x"` and RFC-5987 `filename*=UTF-8''x`.
 */
function filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  // filename*=UTF-8''my%20file.csv  (RFC 5987 — preferred when present)
  const star = /filename\*=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disposition);
  if (star && star[1]) {
    try { return decodeURIComponent(star[1]); } catch { return star[1]; }
  }
  const plain = /filename="?([^"';]+)"?/i.exec(disposition);
  if (plain && plain[1]) return plain[1];
  return fallback;
}

/**
 * Turn an axios blob response into a browser download by creating a temporary
 * object URL and clicking a hidden anchor. Cleans up after itself.
 */
function downloadBlob(res, fallbackName) {
  const disposition = res.headers?.['content-disposition'];
  const name = filenameFromDisposition(disposition, fallbackName);
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has a chance to start the download.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  return name;
}

export default function ExportData() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading]   = useState(true);
  // Tracks which button is mid-download — keyed `<entityKey>:<format>` or 'all'.
  const [busy, setBusy] = useState(null);

  // Password-protect modal. `pwModal.run` is the callback that performs the
  // actual download once a password has been confirmed.
  const [pwModal, setPwModal]   = useState(null); // { title, run } | null
  const [pw, setPw]             = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [showPw, setShowPw]     = useState(false);

  const loadEntities = () => {
    setLoading(true);
    api.get('/export/entities')
      .then(r => setEntities(r.data.entities || []))
      .catch(() => toast.error('Could not load exportable data types'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadEntities(); }, []);

  const totalRecords = entities.reduce((sum, e) => sum + (Number(e.count) || 0), 0);

  /**
   * Open the "Protect this export" modal. `run(password)` is invoked with the
   * confirmed password once the admin clicks Download.
   */
  function promptPassword(title, run) {
    setPw('');
    setPwConfirm('');
    setShowPw(false);
    setPwModal({ title, run });
  }
  function closePwModal() {
    setPwModal(null);
    setPw('');
    setPwConfirm('');
    setShowPw(false);
  }
  const pwValid = pw.length >= 6 && pw === pwConfirm;
  function confirmPassword() {
    if (!pwValid || !pwModal) return;
    const { run } = pwModal;
    const password = pw;
    closePwModal();
    run(password);
  }

  // Download a single entity. CSV downloads immediately (plain text — cannot be
  // encrypted); Excel first prompts for a password and sends it as a header.
  function exportEntity(entity, format) {
    if (format === 'xlsx') {
      promptPassword(`Protect "${entity.label}" export`, pw => runEntityDownload(entity, 'xlsx', pw));
    } else {
      runEntityDownload(entity, 'csv', null);
    }
  }

  async function runEntityDownload(entity, format, password) {
    const key = `${entity.key}:${format}`;
    setBusy(key);
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    const fallback = `${entity.key}-${new Date().toISOString().slice(0, 10)}.${ext}`;
    try {
      const cfg = { responseType: 'blob' };
      if (password) cfg.headers = { 'X-Export-Password': password };
      const res = await api.get(`/export/${entity.key}?format=${format}`, cfg);
      const name = downloadBlob(res, fallback);
      if (password) {
        toast.success(`Downloaded ${entity.label} (${name}). Remember the password — you'll need it to open the file.`);
      } else {
        toast.success(`Downloaded ${entity.label} (${name})`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to export ${entity.label}`);
    } finally {
      setBusy(null);
    }
  }

  // Download the whole-account multi-sheet workbook (always Excel → password).
  function exportAll() {
    promptPassword('Protect full account export', runAllDownload);
  }

  async function runAllDownload(password) {
    setBusy('all');
    const fallback = `rostery-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
    try {
      const res = await api.get('/export/all?format=xlsx', {
        responseType: 'blob',
        headers: { 'X-Export-Password': password },
      });
      const name = downloadBlob(res, fallback);
      toast.success(`Downloaded full export (${name}). Remember the password — you'll need it to open the file.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to export everything');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="w-full space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700
                          flex items-center justify-center shadow">
            <HardDrive size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Export &amp; Backup</h1>
            <p className="text-sm text-slate-500">Download your organisation's data with every field</p>
          </div>
        </div>
        <button
          onClick={loadEntities}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-indigo-600 hover:underline font-medium disabled:opacity-50"
        >
          <RefreshCw size={12} className={clsx(loading && 'animate-spin')} /> Refresh
        </button>
      </div>

      {/* ── Export Everything banner ── */}
      <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl p-6 text-white shadow-sm">
        <div className="flex items-center justify-between gap-5 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Database size={22} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold">Export Everything</p>
              <p className="text-sm text-indigo-100 mt-0.5 leading-relaxed">
                One Excel workbook with every data type on its own sheet — the simplest way to back up
                or move your whole account.
                {!loading && entities.length > 0 && (
                  <> <span className="font-semibold text-white">{totalRecords.toLocaleString()}</span> records across {entities.length} data types.</>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={exportAll}
            disabled={busy === 'all' || loading}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-indigo-700 text-sm font-bold
                       hover:bg-indigo-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm shrink-0"
          >
            {busy === 'all'
              ? <><Loader2 size={16} className="animate-spin" /> Preparing…</>
              : <><FileSpreadsheet size={16} /> Export Everything (Excel)</>}
          </button>
        </div>
      </div>

      {/* ── Privacy explainer ── */}
      <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
        <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-800 leading-relaxed">
          Download your data with every field — for backup, reporting, or moving systems.
          Sensitive fields (passwords, bank/TFN) are excluded.
        </p>
      </div>

      {/* ── Per-entity grid ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shrink-0">
            <FileText size={17} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">Export by data type</h2>
            <p className="text-sm text-slate-500">
              Download a single data type as CSV (one file) or Excel. Every column is included.
            </p>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Lock size={11} className="shrink-0" />
              Excel can be password-protected. CSV is unencrypted — use Excel for a password-protected file.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center gap-2 text-sm text-slate-400">
            <Loader2 size={16} className="animate-spin" /> Loading data types…
          </div>
        ) : entities.length === 0 ? (
          <div className="py-12 text-center">
            <Database size={28} className="text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No exportable data found</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {entities.map(entity => {
              const csvBusy  = busy === `${entity.key}:csv`;
              const xlsxBusy = busy === `${entity.key}:xlsx`;
              const empty    = !Number(entity.count);
              return (
                <div
                  key={entity.key}
                  className="flex flex-col justify-between gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-indigo-300 transition-all"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet size={15} className="text-indigo-500 shrink-0" />
                      <p className="text-sm font-semibold text-slate-900 truncate">{entity.label}</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 ml-6">
                      {Number(entity.count || 0).toLocaleString()} {Number(entity.count) === 1 ? 'record' : 'records'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => exportEntity(entity, 'csv')}
                      disabled={!!busy || empty}
                      title={empty ? 'No records to export' : `Export ${entity.label} as CSV`}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                                 border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50
                                 disabled:cursor-not-allowed transition-colors"
                    >
                      {csvBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} CSV
                    </button>
                    <button
                      onClick={() => exportEntity(entity, 'xlsx')}
                      disabled={!!busy || empty}
                      title={empty ? 'No records to export' : `Export ${entity.label} as Excel`}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                                 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50
                                 disabled:cursor-not-allowed transition-colors"
                    >
                      {xlsxBusy ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />} Excel
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && entities.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-slate-400 pt-2 border-t border-slate-100">
            <Info size={13} className="shrink-0 mt-0.5" />
            <span>
              Exports are scoped to your organisation only and reflect all current records, including archived ones
              where applicable. Large exports may take a moment to prepare.
            </span>
          </div>
        )}
      </div>

      {/* ── Password-protect modal ── */}
      {pwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">

            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
                <Lock size={15} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-800">Protect this export</h3>
                <p className="text-xs text-slate-400 truncate">{pwModal.title}</p>
              </div>
              <button
                onClick={closePwModal}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-xl px-3.5 py-2.5">
                <KeyRound size={15} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  You'll need this password to open the file in Excel. Share it with the recipient
                  separately (not in the same email).
                </p>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={pw}
                    onChange={e => setPw(e.target.value)}
                    autoFocus
                    placeholder="At least 6 characters"
                    className="w-full border border-slate-200 rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:border-indigo-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(s => !s)}
                    title={showPw ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                  >
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Confirm */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Confirm password</label>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') confirmPassword(); }}
                  placeholder="Re-enter password"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                />
                {pwConfirm.length > 0 && pw !== pwConfirm && (
                  <p className="text-xs text-red-500 mt-1">Passwords don't match.</p>
                )}
                {pw.length > 0 && pw.length < 6 && (
                  <p className="text-xs text-red-500 mt-1">Use at least 6 characters.</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button
                onClick={closePwModal}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmPassword}
                disabled={!pwValid}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-indigo-600 text-white
                           hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Lock size={14} /> Download protected file
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
